import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./PromptHelperWidget.css";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  "https://printspaceai.com/api";

// Product types that PrintspaceAI offers (must match backend ALLOWED_ITEM_TYPES / _normalize_item_type)
const PRODUCT_TYPES = ["T-Shirt", "Mug", "Poster", "Sticker", "Hoodie", "Sweatshirt"];

const OCCASIONS = ["Birthday", "Holiday", "Graduation", "Wedding", "Anniversary", "Event", "Everyday", "Gift"];

const STYLES = ["Funny", "Casual", "Formal", "Vintage", "Minimal", "Bold", "Cute", "Cool"];

const INITIAL_FORM = {
  product_type: "",
  occasion: "",
  style: "",
  conversation: [], // Store Q&A pairs
};

const PAGE_GUIDES = [
  {
    match: (path) => path === "/" || path === "/landing",
    title: "Start Here",
    summary: "Pick a category or start with prompt creation.",
    tips: [
      "Use Prompt Assistant to generate your design idea first.",
      "Use Browse Products to pick the merch type you want.",
      "Use Try PrintSpace AI to quickly test your prompt.",
    ],
    actions: [
      { label: "Browse Products", path: "/products" },
      { label: "Open Try Now", path: "/try-now" },
    ],
  },
  {
    match: (path) => path.startsWith("/products") || path.startsWith("/category/"),
    title: "Choose Product",
    summary: "Find the right merch item before designing.",
    tips: [
      "Use search to find by item name or brand.",
      "Use category and brand filters to narrow options.",
      "Click a product tile to open details and start designing.",
    ],
    actions: [{ label: "Go to Products", path: "/products" }],
  },
  {
    match: (path) => path.startsWith("/product/") || path.startsWith("/catalog-product/"),
    title: "Configure Product",
    summary: "Set provider, color, and size before design.",
    tips: [
      "Select provider, color, and size options first.",
      "Click Start Designing to open the designer tool.",
      "Review key features, care instructions, and size guide as needed.",
    ],
    actions: [],
  },
  {
    match: (path) => path.startsWith("/designertool"),
    title: "Build Your Design",
    summary: "Create prompt, upload, preview, adjust, then checkout.",
    tips: [
      "Pick a side tab, then upload image or write prompt.",
      "Use Preview/Generate to render your product mockup.",
      "Use Adjust Layout to move/scale design, then Accept and Preview.",
      "After final preview, click Add to cart or Share to Shop.",
    ],
    actions: [{ label: "Go to Cart", path: "/cart" }],
  },
  {
    match: (path) => path.startsWith("/cart"),
    title: "Cart Review",
    summary: "Confirm quantity and item details before checkout.",
    tips: [
      "Review design, size, color, and quantity.",
      "Remove outdated items before proceeding.",
      "Click Checkout when everything looks correct.",
    ],
    actions: [{ label: "Go to Checkout", path: "/checkout" }],
  },
  {
    match: (path) => path.startsWith("/checkout"),
    title: "Checkout",
    summary: "Complete shipping and payment details.",
    tips: [
      "Fill shipping details carefully.",
      "Double-check total and address before payment.",
      "Place order to finish.",
    ],
    actions: [],
  },
];

const PromptHelperWidget = () => {
  const history = useHistory();
  const location = useLocation();
  
  const [isVisible] = useState(true);
  
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [isGeneratingQuestion, setIsGeneratingQuestion] = useState(false);
  const [isSurpriseMode, setIsSurpriseMode] = useState(false);
  const [askedQuestions, setAskedQuestions] = useState(new Set()); // Track asked questions
  const inputContainerRef = useRef(null);
  const panelRef = useRef(null);
  const bodyScrollRef = useRef(null);

  // ── Recreate Design tab state ──
  const [activeTab, setActiveTab] = useState("create"); // "create" | "recreate" | "guide"
  const [recreateImage, setRecreateImage] = useState(null);        // base64 string
  const [recreateImagePreview, setRecreateImagePreview] = useState(null); // data URL for thumbnail
  const [recreateCustomization, setRecreateCustomization] = useState("");
  const [recreateResult, setRecreateResult] = useState(null);      // prompt string
  const [recreateLoading, setRecreateLoading] = useState(false);
  const recreateFileRef = useRef(null);

  const currentGuide = PAGE_GUIDES.find((g) => g.match(location.pathname)) || {
    title: "Navigation Guide",
    summary: "I can guide you from product selection to checkout.",
    tips: [
      "Tell me what merch you want to create first.",
      "I will suggest the best next page and action.",
      "Use the Guide tab for page-specific instructions.",
    ],
    actions: [{ label: "Go to Landing", path: "/" }],
  };

  // Prompt assistant opens only when user clicks the FAB (no auto-popup)

  // When a new question appears, scroll every scrollable area to the very bottom so the question and answer are visible
  useEffect(() => {
    if (currentQuestion && !isGeneratingQuestion && !loading && open) {
      const scrollAllToBottom = () => {
        const panel = panelRef.current;
        const inputContainer = inputContainerRef.current;
        const input = document.getElementById('prompt-helper-answer-input');

        try {
          // 1. Main panel scrollbar: scroll all the way to bottom
          if (panel) {
            const panelBottom = panel.scrollHeight - panel.clientHeight;
            panel.scrollTo({ top: Math.max(0, panelBottom), behavior: 'smooth' });
          }

          // 2. Inner conversation area (.prompt-helper-conversation) has its own scrollbar; scroll it to bottom
          const conversationEl = panel?.querySelector('.prompt-helper-conversation');
          if (conversationEl && conversationEl.scrollHeight > conversationEl.clientHeight) {
            const convBottom = conversationEl.scrollHeight - conversationEl.clientHeight;
            conversationEl.scrollTo({ top: Math.max(0, convBottom), behavior: 'smooth' });
          }

          // 3. Bring answer input into view at bottom of viewport
          if (inputContainer) {
            inputContainer.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
          }

          if (input && !input.disabled) {
            setTimeout(() => input.focus(), 400);
          }
        } catch (err) {
          console.log("Could not scroll prompt assistant:", err);
        }
      };

      // Wait for DOM/layout, then scroll; run again after a short delay so any late content is included
      let t2Id;
      const t1 = setTimeout(() => {
        requestAnimationFrame(() => {
          scrollAllToBottom();
          t2Id = setTimeout(scrollAllToBottom, 150);
        });
      }, 200);
      return () => {
        clearTimeout(t1);
        if (t2Id) clearTimeout(t2Id);
      };
    }
  }, [currentQuestion, isGeneratingQuestion, loading, open]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Generate follow-up question using ChatGPT
  const generateFollowUpQuestion = async () => {
    // Prevent multiple simultaneous calls
    if (isGeneratingQuestion) {
      return;
    }
    
    setIsGeneratingQuestion(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/v1/prompt-helper/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_type: form.product_type,
          occasion: form.occasion,
          style: form.style,
          conversation: form.conversation,
          asked_questions: Array.from(askedQuestions), // Send previously asked questions
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate question");
      }

      const data = await response.json();
      
      // If ChatGPT thinks we have enough info, generate prompt directly
      if (data.should_generate) {
        setIsGeneratingQuestion(false);
        handleGenerate();
        return;
      }
      
      // Check if this question was already asked
      const questionLower = data.question.toLowerCase().trim();
      if (askedQuestions.has(questionLower)) {
        console.log("Duplicate question detected, generating prompt instead");
        setIsGeneratingQuestion(false);
        handleGenerate();
        return;
      }
      
      // Add to asked questions set
      setAskedQuestions(prev => new Set([...prev, questionLower]));
      setCurrentQuestion(data.question);
      setIsGeneratingQuestion(false);
      // Clear any previous answer when new question appears
      setCurrentAnswer("");
    } catch (err) {
      console.error("Error generating question:", err);
      // Fallback: generate prompt directly if we have some conversation
      if (form.conversation.length >= 2) {
        handleGenerate();
      } else {
        const fallbackQuestion = "Any specific preferences or details you'd like to include?";
        const questionLower = fallbackQuestion.toLowerCase().trim();
        if (!askedQuestions.has(questionLower)) {
          setAskedQuestions(prev => new Set([...prev, questionLower]));
          setCurrentQuestion(fallbackQuestion);
        } else {
          // If even fallback was asked, just generate
          handleGenerate();
        }
      }
      setIsGeneratingQuestion(false);
    }
  };

  // Handle answer to follow-up question
  const handleAnswerSubmit = () => {
    // Prevent multiple submissions
    if (isGeneratingQuestion || loading) {
      return;
    }

    if (!currentAnswer.trim()) {
      setError("Please provide an answer");
      return;
    }

    if (!currentQuestion) {
      setError("No question to answer");
      return;
    }

    // Add current question to asked questions set before adding to conversation
    const questionLower = currentQuestion.toLowerCase().trim();
    setAskedQuestions(prev => new Set([...prev, questionLower]));

    const answerValue = currentAnswer.trim();
    const newConversation = [
      ...form.conversation,
      { question: currentQuestion, answer: answerValue }
    ];

    updateField("conversation", newConversation);
    setCurrentAnswer("");
    setCurrentQuestion(null);
    setError("");

    // Generate next question or final prompt
    generateFollowUpQuestion();
  };

  // Handle "Surprise me" option
  const handleSurpriseMe = async () => {
    setIsSurpriseMode(true);
    setLoading(true);
    setError("");

    try {
      // Generate a random creative prompt
      const randomProduct = PRODUCT_TYPES[Math.floor(Math.random() * PRODUCT_TYPES.length)];
      const randomOccasion = OCCASIONS[Math.floor(Math.random() * OCCASIONS.length)];
      const randomStyle = STYLES[Math.floor(Math.random() * STYLES.length)];

      const response = await fetch(`${API_BASE}/v1/prompt-helper`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: randomProduct,
          occasion: randomOccasion,
          style: randomStyle,
          theme: "creative and unique",
          notes: "Surprise me with something creative and eye-catching",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate surprise prompt");
      }

      const data = await response.json();
      setResult({
        main: data.main,
        variations: data.variations || [],
      });
      setStep(999); // Move to result step
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setError("");

    // Step 0: Product Type
    if (step === 0) {
      if (!form.product_type) {
        setError("Please select a product type");
        return;
      }
      setStep(1);
      return;
    }

    // Step 1: Occasion
    if (step === 1) {
      if (!form.occasion) {
        setError("Please select an occasion");
        return;
      }
      setStep(2);
      return;
    }

    // Step 2: Style
    if (step === 2) {
      if (!form.style) {
        setError("Please select a style");
        return;
      }
      // After 3 questions, start ChatGPT follow-up
      setStep(3);
      generateFollowUpQuestion();
      return;
    }

    // Step 3+: If there's a current question, the Next button should submit the answer
    // This is handled by the handleAnswerSubmit button in the UI
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    setCurrentQuestion(null);

    try {
      // Build context from conversation
      const conversationContext = form.conversation
        .map((q) => `Q: ${q.question}\nA: ${q.answer}`)
        .join("\n\n");

      const response = await fetch(`${API_BASE}/v1/prompt-helper`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: form.product_type,
          occasion: form.occasion,
          style: form.style,
          notes: conversationContext || "Create a custom design prompt",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate prompt");
      }

      const data = await response.json();
      setResult({
        main: data.main,
        variations: data.variations || [],
      });
      setStep(999); // Move to result step
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text) => {
    if (!navigator?.clipboard) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setError("Copied to clipboard!");
        setTimeout(() => setError(""), 2000);
      } catch (err) {
        setError("Copy failed. Please copy manually.");
      }
      document.body.removeChild(textArea);
      return;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setError("");
      setError("Copied to clipboard!");
      setTimeout(() => setError(""), 2000);
      return true;
    } catch (err) {
      setError("Copy failed. Please copy manually.");
      return false;
    }
  };

  const resetFlow = () => {
    setForm(INITIAL_FORM);
    setStep(0);
    setResult(null);
    setError("");
    setCurrentQuestion(null);
    setCurrentAnswer("");
    setIsSurpriseMode(false);
    setAskedQuestions(new Set()); // Reset asked questions
  };

  // ── Recreate Design handlers ──
  const handleRecreateImageUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be under 10 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setRecreateImagePreview(dataUrl);
      setRecreateImage(dataUrl); // full data URI (includes base64 + mime)
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const handleRecreateFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleRecreateImageUpload(file);
    // Reset so the same file can be re-selected
    if (e.target) e.target.value = "";
  };

  const handleRecreateDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleRecreateImageUpload(file);
  };

  const handleRecreateDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRecreateGenerate = async () => {
    if (!recreateImage) {
      setError("Please upload an image first.");
      return;
    }
    setRecreateLoading(true);
    setRecreateResult(null);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/v1/prompt-helper/recreate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_base64: recreateImage,
          customization: recreateCustomization.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to generate recreate prompt");
      }
      const data = await response.json();
      setRecreateResult(data.prompt || "");
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setRecreateLoading(false);
    }
  };

  const resetRecreateFlow = () => {
    setRecreateImage(null);
    setRecreateImagePreview(null);
    setRecreateCustomization("");
    setRecreateResult(null);
    setError("");
  };

  const renderStepContent = () => {
    // Result step
    if (step === 999) {
      return null;
    }

    // Step 0: Product Type
    if (step === 0) {
      return (
        <div className="prompt-helper-step">
          <div className="prompt-helper-label">
            What custom merch would you like to create?
          </div>
          <div className="prompt-helper-chips">
            {PRODUCT_TYPES.map((type) => (
              <button
                key={type}
                className={`prompt-helper-chip ${
                  form.product_type === type ? "selected" : ""
                }`}
                onClick={() => {
                  updateField("product_type", type);
                  setError("");
                }}
                type="button"
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Step 1: Occasion
    if (step === 1) {
      return (
        <div className="prompt-helper-step">
          <div className="prompt-helper-label">
            What's the occasion?
          </div>
          <div className="prompt-helper-chips">
            {OCCASIONS.map((occasion) => (
              <button
                key={occasion}
                className={`prompt-helper-chip ${
                  form.occasion === occasion ? "selected" : ""
                }`}
                onClick={() => {
                  updateField("occasion", occasion);
                  setError("");
                }}
                type="button"
              >
                {occasion}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Step 2: Style
    if (step === 2) {
      return (
        <div className="prompt-helper-step">
          <div className="prompt-helper-label">
            What style are you looking for?
          </div>
          <div className="prompt-helper-chips">
            {STYLES.map((style) => (
              <button
                key={style}
                className={`prompt-helper-chip ${
                  form.style === style ? "selected" : ""
                }`}
                onClick={() => {
                  updateField("style", style);
                  setError("");
                }}
                type="button"
              >
                {style}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Step 3+: ChatGPT follow-up questions
    if (step >= 3) {
      if (isGeneratingQuestion) {
        return (
          <div className="prompt-helper-step">
            <div className="prompt-helper-label" style={{ textAlign: "center" }}>
              Thinking of the perfect question...
            </div>
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <div className="prompt-helper-spinner"></div>
            </div>
          </div>
        );
      }

      if (currentQuestion) {
        return (
          <div 
            className="prompt-helper-step" 
            style={{ 
              pointerEvents: "auto",
              position: "relative",
              zIndex: 10
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="prompt-helper-conversation">
              {form.conversation.map((item, idx) => (
                <div key={idx} className="prompt-helper-qa-pair">
                  <div className="prompt-helper-question">
                    <strong>Q:</strong> {item.question}
                  </div>
                  <div className="prompt-helper-answer">
                    <strong>A:</strong> {item.answer}
                  </div>
                </div>
              ))}
              <div className="prompt-helper-question">
                <strong>Q:</strong> {currentQuestion}
              </div>
            </div>
            <div 
              ref={inputContainerRef}
              style={{ 
                position: "relative", 
                zIndex: 100,
                pointerEvents: "auto",
                marginTop: "12px"
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <input
                type="text"
                id="prompt-helper-answer-input"
                key={`input-${currentQuestion}`} // Force re-render when question changes
                value={currentAnswer || ""}
                onChange={(e) => {
                  e.stopPropagation();
                  const value = e.target.value;
                  setCurrentAnswer(value);
                  setError("");
                }}
                onInput={(e) => {
                  e.stopPropagation();
                  const value = e.target.value;
                  setCurrentAnswer(value);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.focus();
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                }}
                onBlur={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" && currentAnswer.trim() && !isGeneratingQuestion && !loading) {
                    e.preventDefault();
                    handleAnswerSubmit();
                  }
                }}
                placeholder="Your answer..."
                className="prompt-helper-text-input"
                disabled={isGeneratingQuestion || loading}
                style={{
                  pointerEvents: (isGeneratingQuestion || loading) ? "none" : "auto",
                  zIndex: 100,
                  position: "relative",
                  width: "100%",
                  opacity: (isGeneratingQuestion || loading) ? 0.6 : 1,
                  cursor: (isGeneratingQuestion || loading) ? "not-allowed" : "text"
                }}
              />
            </div>
            <div style={{ 
              fontSize: "11px", 
              color: "#9ca3af", 
              marginTop: "8px",
              textAlign: "center"
            }}>
              {isGeneratingQuestion ? "Generating question..." : "Type your answer and click Next"}
            </div>
          </div>
        );
      }

      // Ready to generate
      return (
        <div className="prompt-helper-step">
          <div className="prompt-helper-label" style={{ textAlign: "center" }}>
            Ready to generate your custom design prompt!
          </div>
          {form.conversation.length > 0 && (
            <div className="prompt-helper-conversation" style={{ marginTop: "12px" }}>
              {form.conversation.map((item, idx) => (
                <div key={idx} className="prompt-helper-qa-pair">
                  <div className="prompt-helper-question">
                    <strong>Q:</strong> {item.question}
                  </div>
                  <div className="prompt-helper-answer">
                    <strong>A:</strong> {item.answer}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  if (!isVisible) {
    return null;
  }

  const getStepTitle = () => {
    if (step === 0) return "What custom merch would you like to create?";
    if (step === 1) return "What's the occasion?";
    if (step === 2) return "What style are you looking for?";
    if (step >= 3 && currentQuestion) return "Tell us more...";
    if (step >= 3) return "Ready to generate!";
    if (step === 999) return "Your Prompt";
    return "Prompt Assistant";
  };

  // Show Next button for steps 0-2, or when ready to generate (no current question)
  const showNextButton = (step < 3) || (step >= 3 && !currentQuestion && !isGeneratingQuestion && form.conversation.length === 0);
  // Show Generate button when we have conversation but no current question
  const showGenerateButton = step >= 3 && !currentQuestion && !isGeneratingQuestion && form.conversation.length > 0;

  return (
    <>
      {!open && (
        <button
          type="button"
          className="prompt-helper-launcher"
          title="Assistant"
          aria-label="Open assistant"
          onClick={() => setOpen(true)}
        >
          <span className="prompt-helper-launcher-icon" aria-hidden="true">
            💬
          </span>
          <span className="prompt-helper-launcher-label">Assistant</span>
        </button>
      )}
      <div
        ref={panelRef}
        className={`prompt-helper-panel ${open ? "open" : ""}`}
      >
        <div className="prompt-helper-header">
          <div>
            <div className="prompt-helper-title">Virtual Assistant</div>
            <div className="prompt-helper-subtitle">
              {activeTab === "guide"
                ? `${currentGuide.title}: ${currentGuide.summary}`
                : activeTab === "recreate"
                ? "Upload an image and recreate it with customizations"
                : isSurpriseMode
                  ? "Surprise prompt!"
                  : `Create custom designs for ${form.product_type || "your products"}`}
            </div>
          </div>
          <button
            className="prompt-helper-close"
            type="button"
            title="Minimize to icon"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* ── Tab bar ── */}
        <div className="prompt-helper-tabs">
          <button
            type="button"
            className={`prompt-helper-tab ${activeTab === "create" ? "active" : ""}`}
            onClick={() => { setActiveTab("create"); setError(""); }}
          >
            Create Design
          </button>
          <button
            type="button"
            className={`prompt-helper-tab ${activeTab === "recreate" ? "active" : ""}`}
            onClick={() => { setActiveTab("recreate"); setError(""); }}
          >
            Recreate Design
          </button>
          <button
            type="button"
            className={`prompt-helper-tab ${activeTab === "guide" ? "active" : ""}`}
            onClick={() => { setActiveTab("guide"); setError(""); }}
          >
            Guide
          </button>
        </div>

        {/* ── Create Design tab (existing flow) ── */}
        {activeTab === "create" && step < 999 && (
          <>
            <div className="prompt-helper-steps">
              {["Product", "Occasion", "Style", "Details"].map((label, idx) => (
                <button
                  key={label}
                  className={`prompt-helper-step-btn ${
                    idx === step ? "active" : idx < step ? "completed" : ""
                  }`}
                  type="button"
                  disabled
                >
                  {label}
                </button>
              ))}
            </div>

            <div ref={bodyScrollRef} className="prompt-helper-body">
              <div className="prompt-helper-step-title">{getStepTitle()}</div>
              {renderStepContent()}
            </div>

            <div className="prompt-helper-actions">
              {step === 0 ? (
                <>
                  <button
                    type="button"
                    className="prompt-helper-surprise"
                    onClick={handleSurpriseMe}
                    disabled={loading}
                  >
                    {loading ? "Surprising..." : "🎲 Surprise Me!"}
                  </button>
                  <button
                    type="button"
                    className="prompt-helper-primary"
                    onClick={handleNext}
                    disabled={!form.product_type}
                  >
                    Next
                  </button>
                </>
              ) : (
                <>
                  {step > 0 && (
                    <button
                      type="button"
                      className="prompt-helper-secondary"
                      onClick={() => {
                        if (step > 0) {
                          setStep(step - 1);
                          setError("");
                          setCurrentQuestion(null);
                          setCurrentAnswer("");
                        }
                      }}
                      disabled={step === 0 || isGeneratingQuestion}
                    >
                      Back
                    </button>
                  )}
                  {step >= 3 && currentQuestion && (
                    <button
                      type="button"
                      className="prompt-helper-secondary"
                      onClick={() => {
                        // Generate with current conversation, even if empty
                        handleGenerate();
                      }}
                      disabled={loading}
                      style={{ fontSize: "12px", padding: "8px" }}
                    >
                      That's enough
                    </button>
                  )}
                  {/* Next button for answering ChatGPT questions */}
                  {step >= 3 && currentQuestion && (
                    <button
                      type="button"
                      className="prompt-helper-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleAnswerSubmit();
                      }}
                      disabled={!currentAnswer.trim() || isGeneratingQuestion || loading}
                      style={{
                        pointerEvents: (!currentAnswer.trim() || isGeneratingQuestion || loading) ? "none" : "auto",
                        cursor: (!currentAnswer.trim() || isGeneratingQuestion || loading) ? "not-allowed" : "pointer",
                        opacity: (!currentAnswer.trim() || isGeneratingQuestion || loading) ? 0.6 : 1
                      }}
                    >
                      {isGeneratingQuestion ? "Processing..." : "Next"}
                    </button>
                  )}
                  {/* Next button for initial steps (0-2) */}
                  {showNextButton && (
                    <button
                      type="button"
                      className="prompt-helper-primary"
                      onClick={handleNext}
                      disabled={isGeneratingQuestion}
                    >
                      Next
                    </button>
                  )}
                  {/* Generate button when ready */}
                  {showGenerateButton && (
                    <button
                      type="button"
                      className="prompt-helper-primary"
                      onClick={handleGenerate}
                      disabled={loading}
                    >
                      {loading ? "Generating..." : "Generate Prompt"}
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {error && (
          <div className={`prompt-helper-error ${error.includes("Copied") ? "success" : ""}`}>
            {error}
          </div>
        )}

        {/* ── Create Design result ── */}
        {activeTab === "create" && result && step === 999 && (
          <div className="prompt-helper-result">
            <div className="prompt-helper-result-row">
              <div className="prompt-helper-result-label">Your Custom Prompt</div>
              <button
                type="button"
                onClick={() => copyText(result.main)}
                className="prompt-helper-copy"
              >
                Copy
              </button>
            </div>
            <div className="prompt-helper-result-text">{result.main}</div>

            {result.variations && result.variations.length > 0 && (
              <>
                <div className="prompt-helper-result-label" style={{ marginTop: "16px" }}>
                  Variations
                </div>
                {result.variations.map((v, idx) => (
                  <div key={idx} className="prompt-helper-variation">
                    <span>{v}</span>
                    <button
                      type="button"
                      onClick={() => copyText(v)}
                      className="prompt-helper-copy"
                    >
                      Copy
                    </button>
                  </div>
                ))}
              </>
            )}

            <div className="prompt-helper-footer">
              <button
                type="button"
                className="prompt-helper-secondary"
                onClick={resetFlow}
              >
                Create Another
              </button>
              <button
                type="button"
                className="prompt-helper-primary"
                onClick={async () => {
                  const copied = await copyText(result.main);
                  setTimeout(() => {
                    history.push('/products');
                    setOpen(false);
                  }, copied ? 1500 : 500);
                }}
              >
                Copy & Browse Products
              </button>
            </div>
          </div>
        )}

        {/* ── Recreate Design tab ── */}
        {activeTab === "recreate" && (
          <div className="prompt-helper-body recreate-body">
            {!recreateResult ? (
              <>
                <div className="prompt-helper-step-title">Upload an image to recreate</div>

                {/* Image upload / preview */}
                {recreateImagePreview ? (
                  <div className="recreate-preview">
                    <img src={recreateImagePreview} alt="Uploaded design" />
                    <button
                      type="button"
                      className="recreate-preview-remove"
                      onClick={() => {
                        setRecreateImage(null);
                        setRecreateImagePreview(null);
                      }}
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div
                    className="recreate-upload-area"
                    onClick={() => recreateFileRef.current?.click()}
                    onDrop={handleRecreateDrop}
                    onDragOver={handleRecreateDragOver}
                  >
                    <div className="recreate-upload-icon">+</div>
                    <div className="recreate-upload-text">
                      Click or drag an image here
                    </div>
                    <div className="recreate-upload-hint">PNG, JPG up to 10 MB</div>
                    <input
                      ref={recreateFileRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleRecreateFileChange}
                    />
                  </div>
                )}

                {/* Customization */}
                <div className="prompt-helper-label" style={{ marginTop: "16px" }}>
                  Customizations (optional)
                </div>
                <textarea
                  className="recreate-textarea"
                  placeholder="Describe changes you'd like... e.g. make it more colorful, add hearts, change text to Happy Birthday"
                  value={recreateCustomization}
                  onChange={(e) => setRecreateCustomization(e.target.value)}
                  rows={3}
                />

                {/* Actions */}
                <div className="prompt-helper-actions" style={{ marginTop: "16px" }}>
                  <button
                    type="button"
                    className="prompt-helper-primary"
                    onClick={handleRecreateGenerate}
                    disabled={!recreateImage || recreateLoading}
                  >
                    {recreateLoading ? "Analyzing image..." : "Generate Prompt"}
                  </button>
                </div>
              </>
            ) : (
              /* ── Recreate result ── */
              <div className="prompt-helper-result">
                <div className="prompt-helper-result-row">
                  <div className="prompt-helper-result-label">Recreated Prompt</div>
                  <button
                    type="button"
                    onClick={() => copyText(recreateResult)}
                    className="prompt-helper-copy"
                  >
                    Copy
                  </button>
                </div>
                <div className="prompt-helper-result-text">{recreateResult}</div>

                {recreateImagePreview && (
                  <div className="recreate-result-thumb">
                    <img src={recreateImagePreview} alt="Original" />
                  </div>
                )}

                <div className="prompt-helper-footer">
                  <button
                    type="button"
                    className="prompt-helper-secondary"
                    onClick={resetRecreateFlow}
                  >
                    Try Another
                  </button>
                  <button
                    type="button"
                    className="prompt-helper-primary"
                    onClick={async () => {
                      await copyText(recreateResult);
                    }}
                  >
                    Copy Prompt
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "guide" && (
          <div className="prompt-helper-body">
            <div className="prompt-helper-step-title">{currentGuide.title}</div>
            <div className="prompt-helper-label" style={{ marginBottom: "10px" }}>
              {currentGuide.summary}
            </div>
            <div className="prompt-helper-guide-list">
              {currentGuide.tips.map((tip, idx) => (
                <div className="prompt-helper-guide-item" key={`tip-${idx}`}>
                  {idx + 1}. {tip}
                </div>
              ))}
            </div>
            {currentGuide.actions?.length > 0 && (
              <div className="prompt-helper-actions" style={{ marginTop: "12px" }}>
                {currentGuide.actions.map((action) => (
                  <button
                    key={`${action.path}-${action.label}`}
                    type="button"
                    className="prompt-helper-primary"
                    onClick={() => {
                      history.push(action.path);
                      setOpen(false);
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PromptHelperWidget;
